/**
 * HTTP utilities for AgentMeter SDK
 * HTTP request utilities with retry logic and error handling
 */

import { AgentMeterError, NetworkError } from '../types';

/**
 * HTTP client configuration
 */
export interface HttpConfig {
  baseUrl: string;
  apiKey: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
  debug: boolean;
}

/**
 * HTTP response interface
 */
export interface HttpResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Extract headers from Response object
 */
function extractHeaders(response: Response): Record<string, string> {
  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });
  return headers;
}

/**
 * HTTP client with automatic retries and error handling
 */
export class HttpClient {
  private config: HttpConfig;

  constructor(config: HttpConfig) {
    this.config = config;
  }

  /**
   * Make HTTP request with retries
   */
  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<HttpResponse<T>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    let lastError: Error;

    for (let attempt = 0; attempt <= this.config.retryAttempts; attempt++) {
      try {
        if (this.config.debug && attempt > 0) {
          console.log(`AgentMeter SDK: Retry attempt ${attempt} for ${url}`);
        }

        const response = await this.makeRequest<T>(url, options);
        
        if (this.config.debug) {
          console.log(`AgentMeter SDK: ${options.method || 'GET'} ${url} -> ${response.status}`);
        }

        return response;
      } catch (error) {
        lastError = error as Error;
        
        if (this.config.debug) {
          console.error(`AgentMeter SDK: Request failed (attempt ${attempt + 1}):`, error);
        }

        // Don't retry for client errors (4xx) except 429 (rate limit)
        if (error instanceof AgentMeterError && error.statusCode) {
          if (error.statusCode >= 400 && error.statusCode < 500 && error.statusCode !== 429) {
            throw error;
          }
        }

        // Don't retry on the last attempt
        if (attempt === this.config.retryAttempts) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay = this.config.retryDelay * Math.pow(2, attempt);
        await sleep(delay);
      }
    }

    throw lastError!;
  }

  /**
   * Make a single HTTP request
   */
  private async makeRequest<T>(
    url: string,
    options: RequestInit
  ): Promise<HttpResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Content-Type': 'application/json',
          'User-Agent': 'AgentMeter-SDK-TypeScript/1.0.0',
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const headers = extractHeaders(response);
      let data: T;

      try {
        const responseText = await response.text();
        data = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        throw new AgentMeterError(
          'Failed to parse response JSON',
          'PARSE_ERROR',
          response.status,
          { parseError }
        );
      }

      if (!response.ok) {
        // Try to extract error information from response
        let errorCode = 'HTTP_ERROR';
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorDetails = data;

        if (data && typeof data === 'object') {
          const errorData = data as any;
          if (errorData.error) {
            errorCode = errorData.error.code || errorCode;
            errorMessage = errorData.error.message || errorMessage;
            errorDetails = errorData.error.details || errorDetails;
          }
        }

        throw new AgentMeterError(
          errorMessage,
          errorCode,
          response.status,
          errorDetails
        );
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof AgentMeterError) {
        throw error;
      }

      if ((error as Error).name === 'AbortError') {
        throw new NetworkError(
          `Request timeout after ${this.config.timeout}ms`,
          { timeout: this.config.timeout }
        );
      }

      throw new NetworkError(
        `Network request failed: ${(error as Error).message}`,
        { originalError: error }
      );
    }
  }

  /**
   * Helper method for GET requests
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<HttpResponse<T>> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return this.request<T>(url, { method: 'GET' });
  }

  /**
   * Helper method for POST requests
   */
  async post<T>(endpoint: string, body?: any): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Helper method for PUT requests
   */
  async put<T>(endpoint: string, body?: any): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Helper method for DELETE requests
   */
  async delete<T>(endpoint: string): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}